import * as logger from 'universal-logger';
import { defineMessages } from 'react-intl';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { filterSetFilters } from 'redux/saga/selectors';
import { selectIsClient } from 'redux/modules/device';
import { SEARCH } from 'constants/filter-sets';
import { isEmpty } from 'helpers/verify';
import { setIsSearchable, setPopup, clearPopup, setViewport, selectViewport } from './SearchMap.reducer';
import { SearchLocation } from '../models/SearchLocation';
import { setSelectedLocation } from '../SearchBox/SearchBox.reducer';
import { getUserLocationForSearch, selectLoadingFromResultsLayout } from '../Search.helpers';
import { importMapboxDependencies, getEncodedBoundsForViewport } from './SearchMap.helpers';
import { LOCATION_TYPES } from './SearchMap.constants';
import { getSelectedRadius } from '../filters/filters.saga';
import { selectRedoSearchClickedFromVenueSupply } from '../Search.reducer';

export const MAP_MOVED = 'classpass/searchmap/MAP_MOVED';
export const MAP_SEARCH = 'classpass/searchmap/MAP_SEARCH';
export const MARKER_SELECTED = 'classpass/searchmap/MARKER_SELECTED';
export const UPDATE_VIEWPORT = 'classpass/searchmap/UPDATE_VIEWPORT';

export const updateViewport = (width, height) => ({ type: UPDATE_VIEWPORT, mapDimension: { width, height } });
export const onMapMove = viewport => ({ type: MAP_MOVED, viewport });
export const onMapSearch = mapBound => ({ type: MAP_SEARCH, mapBound });
export const onMarkerSelected = venue => ({ type: MARKER_SELECTED, venue });

export const messages = defineMessages({
  mappedArea: {
    id: 'searchMap.saga.search.mappedArea',
    defaultMessage: 'Mapped area',
  },
});

export function* doMapMoved({ viewport }) {
  const isLoadingSearchResults = yield select(selectLoadingFromResultsLayout);
  if (isLoadingSearchResults) {
    return;
  }

  yield all([put(setIsSearchable(true)), put(setViewport(viewport))]);
}

export function* resetMap() {
  yield all([put(setIsSearchable(false)), put(clearPopup())]);
}

export function* doMapSearch({ mapBound }) {
  const [isLoadingResults, viewport] = yield all([select(selectLoadingFromResultsLayout), select(selectViewport)]);

  if (isLoadingResults) {
    return;
  }

  yield call(resetMap);
  yield put(
    setSelectedLocation(
      new SearchLocation({
        map_bounds: mapBound,
        lat: viewport.latitude,
        lon: viewport.longitude,
        name: messages.mappedArea,
        type: LOCATION_TYPES.mapSearch,
      })
    )
  );
}

export function* getMapBoundsForViewport(location, radius) {
  try {
    const [isClient, viewportState] = yield all([yield select(selectIsClient), yield select(selectViewport)]);

    if (!isEmpty(viewportState) && isClient) {
      const {
        Mapbox: { LngLat },
        ViewportMercatorProject: { WebMercatorViewport },
      } = yield call(importMapboxDependencies);
      const viewport = new WebMercatorViewport(viewportState);
      const bounds = new LngLat(location.lon, location.lat).toBounds(radius * 1000);
      const fitViewport = viewport.fitBounds(bounds.toArray());
      const viewportMapBounds = getEncodedBoundsForViewport(fitViewport);
      return viewportMapBounds;
    }
  } catch (err) {
    yield call(logger.warn, 'Failed to get map bounds for search map', err);
  }
  return null;
}

export function* doUpdateViewport({ mapDimension }) {
  try {
    const [
      {
        Mapbox: { LngLat },
        ViewportMercatorProject: { WebMercatorViewport },
      },
      location = {},
      filters,
      wasRedoSearchClickedFromVenueSupply,
    ] = yield all([
      call(importMapboxDependencies),
      select(getUserLocationForSearch),
      select(filterSetFilters, SEARCH),
      select(selectRedoSearchClickedFromVenueSupply),
    ]);

    const hasLocation = location.lat && location.lon;
    // Dont update the viewport if its a map search or if we don't have the location
    if (location.isMapSearch || !hasLocation) {
      return;
    }

    const mercatorViewport = new WebMercatorViewport(mapDimension);
    let radius = getSelectedRadius(filters) || location.calculatedRadiusKm;
    if (wasRedoSearchClickedFromVenueSupply) {
      radius = location.calculatedRadiusKm;
    }
    const bounds = new LngLat(location.lon, location.lat).toBounds(radius * 1000).toArray();
    const viewport = mercatorViewport.fitBounds(bounds);
    yield put(setViewport(viewport));
    yield call(resetMap);
  } catch (err) {
    yield call(logger.warn, 'Failed to update zoom on search map', err);
  }
}

export function* doMarkerSelected(action) {
  yield put(setPopup(action.venue));
}

export function* watchSearchMap() {
  yield takeLatest(MAP_MOVED, doMapMoved);
  yield takeLatest(MAP_SEARCH, doMapSearch);
  yield takeLatest(MARKER_SELECTED, doMarkerSelected);
  yield takeLatest(UPDATE_VIEWPORT, doUpdateViewport);
}
